import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatusBadge, ClientLogo } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { LayoutGrid, Table as TableIcon, AlertTriangle } from "lucide-react";
import { STATUS_OPTIONS } from "@/lib/statuses";
import PriorityChip from "@/components/PriorityChip";
import { fmtDate, fmtDateTime } from "@/lib/dates";
import TaskDetailDialog from "@/components/project/TaskDetailDialog";
export default function MyTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [view, setView] = useState("table"); // 'table' | 'cards'

  const isAdminOrManager = ["Admin", "Manager"].includes(user.role);
  const isAdmin = user.role === "Admin";

  const [filters, setFilters] = useState({
    status: "open", // 'open' | 'Not Started' | 'In Progress' | 'Completed' | 'all'
    client_id: "all",
    sort: "due_asc",
    q: "",
    mine: "assigned", // 'assigned' | 'created' | 'both'
    priority: "all", // 'all' | 'High' | 'Medium' | 'Low'
  });

  useEffect(() => {
    (async () => {
      const [p, c, u] = await Promise.all([
        api.get("/projects", { params: { view: "all" } }),
        api.get("/clients"),
        api.get("/users"),
      ]);
      setProjects(p.data || []);
      setClients(c.data || []);
      setUsers(u.data || []);
    })();
  }, []);

  const reloadTasks = async () => {
    const t = await api.get("/tasks", { params: { mine: filters.mine, view: "all" } });
    setTasks(t.data || []);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      const t = await api.get("/tasks", { params: { mine: filters.mine, view: "all" } });
      if (active) setTasks(t.data || []);
    })();
    return () => { active = false; };
  }, [filters.mine]);

  const myProjects = useMemo(
    () => projects.filter((p) => (p.assignee_ids || []).includes(user.user_id)),
    [projects, user]
  );

  const filtered = useMemo(() => {
    let list = [...tasks];
    if (filters.status === "open") list = list.filter((t) => t.status !== "Completed");
    else if (filters.status !== "all") list = list.filter((t) => t.status === filters.status);
    if (filters.priority !== "all") list = list.filter((t) => (t.priority || "Medium") === filters.priority);
    if (filters.client_id !== "all") {
      const projIds = new Set(projects.filter((p) => p.client_id === filters.client_id).map((p) => p.id));
      list = list.filter((t) => projIds.has(t.project_id));
    }
    if (filters.q) {
      const q = filters.q.toLowerCase();
      list = list.filter((t) => t.title.toLowerCase().includes(q));
    }
    const projMap = new Map(projects.map((p) => [p.id, p]));
    const clientMap = new Map(clients.map((c) => [c.id, c]));
    if (filters.sort === "due_asc") {
      list.sort((a, b) => (a.due_date || "9999") > (b.due_date || "9999") ? 1 : -1);
    } else if (filters.sort === "due_desc") {
      list.sort((a, b) => (a.due_date || "0000") < (b.due_date || "0000") ? 1 : -1);
    } else if (filters.sort === "title") {
      list.sort((a, b) => a.title.localeCompare(b.title));
    }
    return list.map((t) => ({ ...t, _project: projMap.get(t.project_id), _client: clientMap.get(projMap.get(t.project_id)?.client_id) }));
  }, [tasks, projects, filters, clients]);

  const clientById = (id) => clients.find((c) => c.id === id);

  const myProjectsFiltered = useMemo(() => {
    let list = [...myProjects];
    if (filters.status === "open") list = list.filter((p) => p.status !== "Completed");
    else if (filters.status !== "all" && ["Not Started", "In Progress", "On Hold", "Suspended", "Completed"].includes(filters.status)) {
      list = list.filter((p) => p.status === filters.status);
    }
    if (filters.client_id !== "all") list = list.filter((p) => p.client_id === filters.client_id);
    if (filters.q) {
      const q = filters.q.toLowerCase();
      list = list.filter((p) => p.title.toLowerCase().includes(q));
    }
    list.sort((a, b) => (a.due_date || "9999") > (b.due_date || "9999") ? 1 : -1);
    return list;
  }, [myProjects, filters]);

  return (
    <div className="min-h-screen bg-white" data-testid="my-work-page">
      <PageHeader title="My Work" subtitle="Tasks & Projects involving me" />

      <div className="px-4 sm:px-8 py-6">
        {/* Mine mode chip group */}
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden mb-5" data-testid="filter-mine">
          {[
            { key: "assigned", label: "Assigned to me" },
            { key: "created", label: "Created by me" },
            { key: "both", label: "Both" },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setFilters({ ...filters, mine: m.key })}
              className={`px-3 py-1.5 text-xs ${filters.mine === m.key ? "bg-[#0a2540] text-white" : "bg-white text-slate-600 hover:text-[#0a2540]"}`}
              data-testid={`mine-${m.key}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-2 sm:gap-3 mb-5">
          <div className="w-full sm:w-56">
            <div className="gc-label mb-1">Search</div>
            <Input className="rounded-sm" placeholder="title…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="filter-q" />
          </div>
          <div className="flex-1 min-w-[140px] sm:flex-none">
            <div className="gc-label mb-1">Status</div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-full sm:w-44 rounded-sm" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open (default)</SelectItem>
                <SelectItem value="all">All</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[120px] sm:flex-none">
            <div className="gc-label mb-1">Priority</div>
            <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
              <SelectTrigger className="w-full sm:w-36 rounded-sm" data-testid="filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[160px] sm:flex-none">
            <div className="gc-label mb-1">Client</div>
            <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v })}>
              <SelectTrigger className="w-full sm:w-56 rounded-sm" data-testid="filter-client"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[140px] sm:flex-none">
            <div className="gc-label mb-1">Sort</div>
            <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
              <SelectTrigger className="w-full sm:w-44 rounded-sm" data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="due_asc">Due date ↑</SelectItem>
                <SelectItem value="due_desc">Due date ↓</SelectItem>
                <SelectItem value="title">Title A–Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-auto sm:ml-auto inline-flex border border-slate-200 rounded-sm overflow-hidden">
            <button
              onClick={() => setView("table")}
              className={`flex-1 sm:flex-none justify-center px-3 py-1.5 text-xs inline-flex items-center gap-1 ${view === "table" ? "bg-[#0a2540] text-white" : "bg-white text-slate-600"}`}
              data-testid="view-table"
            >
              <TableIcon size={12} /> Table
            </button>
            <button
              onClick={() => setView("cards")}
              className={`flex-1 sm:flex-none justify-center px-3 py-1.5 text-xs inline-flex items-center gap-1 ${view === "cards" ? "bg-[#0a2540] text-white" : "bg-white text-slate-600"}`}
              data-testid="view-cards"
            >
              <LayoutGrid size={12} /> Cards
            </button>
          </div>
        </div>

        <Tabs defaultValue="tasks">
          <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 overflow-x-auto flex-nowrap">
            <TabsTrigger value="tasks" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm">Tasks ({filtered.length})</TabsTrigger>
            <TabsTrigger value="projects" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm">Projects ({myProjectsFiltered.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="tasks" className="pt-5">
            {view === "table" ? (
              <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2.5">Task</th>
                      <th className="text-left px-4 py-2.5">Project</th>
                      <th className="text-left px-4 py-2.5">Priority</th>
                      <th className="text-left px-4 py-2.5">Status</th>
                      <th className="text-left px-4 py-2.5">Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-slate-400">No matching tasks</td></tr>}
                    {filtered.map((t) => (
                      <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`mywork-task-${t.id}`}>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setActiveTask(t)}
                            className="font-medium text-[#0a2540] hover:underline text-left"
                            data-testid={`mywork-open-task-${t.id}`}
                          >
                            {t.title}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          {t._project && (
                            <Link to={`/projects/${t.project_id}`} className="inline-flex items-center gap-2 text-xs text-slate-700 hover:text-[#0a2540]">
                              {t._client && <ClientLogo fileId={t._client.logo_file_id} name={t._client.name} size={20} />}
                              <span>{t._project.title}</span>
                            </Link>
                          )}
                        </td>
                        <td className="px-4 py-3"><PriorityChip priority={t.priority} /></td>
                        <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                        <td className="px-4 py-3 font-mono-data text-xs">{fmtDate(t.due_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.length === 0 && <div className="col-span-full text-center text-xs text-slate-400 py-10">No matching tasks</div>}
                {filtered.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTask(t)}
                    className="border border-slate-200 p-4 hover:border-[#0a2540] block text-left w-full"
                    data-testid={`mywork-task-card-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        {t._client && <ClientLogo fileId={t._client.logo_file_id} name={t._client.name} size={28} />}
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-[#0a2540] truncate">{t.title}</div>
                          <div className="mt-0.5 text-[10px] text-slate-500">{t._client?.name || ""}</div>
                        </div>
                      </div>
                      <StatusBadge status={t.status} />
                    </div>
                    <div className="mt-2 text-xs text-slate-500">{t._project?.title}</div>
                    <div className="mt-3 flex items-center gap-2 justify-between">
                      <PriorityChip priority={t.priority} />
                      <div className="font-mono-data text-[11px] text-slate-600">Due: {fmtDate(t.due_date)}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="projects" className="pt-5">
            {view === "table" ? (
              <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2.5">Project</th>
                      <th className="text-left px-4 py-2.5">Status</th>
                      <th className="text-left px-4 py-2.5">Stuck</th>
                      <th className="text-left px-4 py-2.5">Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myProjectsFiltered.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-xs text-slate-400">No projects</td></tr>}
                    {myProjectsFiltered.map((p) => (
                      <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <Link to={`/projects/${p.id}`} className="inline-flex items-center gap-2.5 font-medium text-[#0a2540] hover:underline">
                            <ClientLogo fileId={clientById(p.client_id)?.logo_file_id} name={clientById(p.client_id)?.name} size={20} />
                            <span>{p.title}</span>
                          </Link>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                        <td className="px-4 py-3 text-xs">{p.stuck_with === "None" ? "—" : <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={10} />{p.stuck_with}</span>}</td>
                        <td className="px-4 py-3 font-mono-data text-xs">{fmtDate(p.due_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {myProjectsFiltered.map((p) => (
                  <Link key={p.id} to={`/projects/${p.id}`} className="border border-slate-200 p-4 hover:border-[#0a2540] block">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <ClientLogo fileId={clientById(p.client_id)?.logo_file_id} name={clientById(p.client_id)?.name} size={28} />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-[#0a2540] truncate">{p.title}</div>
                          <div className="text-[10px] text-slate-500">{clientById(p.client_id)?.name || ""}</div>
                        </div>
                      </div>
                      <StatusBadge status={p.status} />
                    </div>
                    <div className="mt-3 font-mono-data text-[11px] text-slate-600">Due: {fmtDate(p.due_date)}</div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <TaskDetailDialog
        task={activeTask}
        users={users}
        client={activeTask ? clients.find((c) => c.id === projects.find((p) => p.id === activeTask.project_id)?.client_id) : null}
        canFullEdit={isAdminOrManager}
        canDelete={isAdmin}
        onClose={() => setActiveTask(null)}
        onChanged={reloadTasks}
      />
    </div>
  );
}
